import { isTruthy } from '@ardoq/common-helpers';
import {
  ComponentGraphGroup,
  ComponentGraphNode,
  getGraphNodeOrGroupLabelForViewpointMode,
  GraphInterface,
  GraphItemsModel,
  ItemLabels,
  LabelsInterface,
  RelationshipDiagramsViewInterface,
  RepresentationInterface,
  SVGColorAttributes,
  TraversalAndGroupingResult,
  URLFieldValue,
} from '@ardoq/graph';
import { APIComponentAttributes } from '@ardoq/api-types';
import { toIdSet } from '@ardoq/core';
import {
  ConditionalFormattingState,
  RepresentationData,
} from '@ardoq/data-model';
import type { I18nLocale } from '@ardoq/i18n';
import { GetRepresentationData } from 'tabview/relationshipsDiagramsViewModel/types';
import {
  addUpdated,
  getComponentRepresentationDataEntries,
  getComponentUrlFieldValueEntries,
  getGraphItemColorEntries,
  getGraphItemConditionalFormattingStateEntries,
  getItems,
  getUpdatedItems,
  setGraphItemClassProperties,
} from './graphItemUpdateUtils';

const getComponentItemLabelsEntry = (
  viewInterface: LabelsInterface & GraphInterface,
  itemId: string,
  items: GraphItemsModel<ComponentGraphNode | ComponentGraphGroup>,
  rawGraph: TraversalAndGroupingResult,
  i18nLocale: I18nLocale
): [string, ItemLabels] | null => {
  const itemsResult = getItems(itemId, items, rawGraph);
  if (!itemsResult) return null;

  const { graphItem, rawGraphItem } = itemsResult;
  if (!rawGraphItem) return null;

  const itemLabels = getGraphNodeOrGroupLabelForViewpointMode(
    viewInterface,
    rawGraphItem,
    i18nLocale
  );

  if (!itemLabels) {
    return null;
  }
  return [graphItem.id, itemLabels];
};

type GetUpdatedGraphItemDataArgs = {
  updatedNodeIds: Set<string>;
  updatedGroupIds: Set<string>;
  nodes: GraphItemsModel<ComponentGraphNode>;
  groups: GraphItemsModel<ComponentGraphGroup>;
  rawGraph: TraversalAndGroupingResult;
  viewInterface: LabelsInterface & GraphInterface & RepresentationInterface;
  getRepresentationData: GetRepresentationData;
  i18nLocale: I18nLocale;
};
const getUpdatedGraphItemDataEntries = ({
  updatedNodeIds,
  updatedGroupIds,
  nodes,
  groups,
  rawGraph,
  viewInterface,
  getRepresentationData,
  i18nLocale,
}: GetUpdatedGraphItemDataArgs) => {
  const nodeLabelsData = [...updatedNodeIds]
    .map(itemId =>
      getComponentItemLabelsEntry(
        viewInterface,
        itemId,
        nodes,
        rawGraph,
        i18nLocale
      )
    )
    .filter(isTruthy);

  const groupLabelsData = [...updatedGroupIds]
    .map(itemId =>
      getComponentItemLabelsEntry(
        viewInterface,
        itemId,
        groups,
        rawGraph,
        i18nLocale
      )
    )
    .filter(isTruthy);

  const urlFieldValuesData = getComponentUrlFieldValueEntries(
    viewInterface,
    updatedNodeIds,
    updatedGroupIds,
    nodes,
    groups
  );

  const representationDataByComponentId = getComponentRepresentationDataEntries(
    updatedNodeIds,
    updatedGroupIds,
    nodes,
    groups,
    getRepresentationData
  );

  const nodeColorsByGraphItemId = getGraphItemColorEntries(
    viewInterface,
    updatedNodeIds,
    updatedGroupIds,
    nodes,
    groups
  );

  const conditionalFormattingState =
    getGraphItemConditionalFormattingStateEntries(
      viewInterface,
      updatedNodeIds,
      updatedGroupIds,
      nodes,
      groups
    );

  return {
    labelsData: [...nodeLabelsData, ...groupLabelsData],
    urlFieldValuesByComponentId: urlFieldValuesData,
    representationDataByComponentId,
    nodeColorsByGraphItemId,
    conditionalFormattingState,
  };
};

type HandleViewpointComponentsUpdateArgs = {
  viewInterface: RelationshipDiagramsViewInterface;
  rawGraph: TraversalAndGroupingResult;
  getRepresentationData: GetRepresentationData;
  nodes: GraphItemsModel<ComponentGraphNode>;
  groups: GraphItemsModel<ComponentGraphGroup>;
  nodeLabelsData: Map<string, ItemLabels>;
  urlFieldValuesByComponentId: Map<string, URLFieldValue[] | null>;
  representationDataByComponentId: Map<string, RepresentationData | null>;
  nodeColorsByGraphItemId: Map<string, SVGColorAttributes>;
  conditionalFormattingState: Map<string, ConditionalFormattingState>;
  i18nLocale: I18nLocale;
};

export const handleViewpointComponentsUpdate = <
  TState extends HandleViewpointComponentsUpdateArgs,
>(
  state: TState,
  components: APIComponentAttributes[]
): TState => {
  const {
    viewInterface,
    rawGraph,
    getRepresentationData,
    nodeLabelsData: previousNodeLabelsData,
    nodes,
    groups,
    urlFieldValuesByComponentId: previousUrlFieldValuesByComponentId,
    representationDataByComponentId: previousRepresentationDataByComponentId,
    nodeColorsByGraphItemId: previousColorsByGraphItemId,
    conditionalFormattingState: previousConditionalFormattingState,
  } = state;

  const updatedModelIds = toIdSet(components);

  const updatedGroupIds = getUpdatedItems(groups, updatedModelIds);
  const updatedNodeIds = getUpdatedItems(nodes, updatedModelIds);

  const newGraphItemData = getUpdatedGraphItemDataEntries({
    updatedNodeIds,
    updatedGroupIds,
    nodes,
    groups,
    rawGraph,
    viewInterface,
    getRepresentationData,
    i18nLocale: state.i18nLocale,
  });

  // Merge old data with fresh data for updated items
  const nodeLabelsData = new Map<string, ItemLabels>([
    ...previousNodeLabelsData,
    ...newGraphItemData.labelsData,
  ]);
  const urlFieldValuesByComponentId = new Map<string, URLFieldValue[] | null>([
    ...previousUrlFieldValuesByComponentId,
    ...newGraphItemData.urlFieldValuesByComponentId,
  ]);

  const representationDataByComponentId = new Map<
    string,
    RepresentationData | null
  >([
    ...previousRepresentationDataByComponentId,
    ...newGraphItemData.representationDataByComponentId,
  ]);

  const nodeColorsByGraphItemId = new Map<string, SVGColorAttributes>([
    ...previousColorsByGraphItemId,
    ...newGraphItemData.nodeColorsByGraphItemId,
  ]);

  const conditionalFormattingState = new Map<
    string,
    ConditionalFormattingState
  >([
    ...previousConditionalFormattingState,
    ...newGraphItemData.conditionalFormattingState,
  ]);

  setGraphItemClassProperties({
    itemIds: [...updatedGroupIds, ...updatedNodeIds],
    nodes,
    groups,
    nodeLabelsData,
    urlFieldValuesByComponentId,
    representationDataByComponentId,
    nodeColorsByGraphItemId,
    conditionalFormattingState,
  });

  return {
    ...state,
    nodes: addUpdated(nodes, updatedNodeIds),
    groups: addUpdated(groups, updatedGroupIds),
    expandedStateChangingGroupId: null,
    urlFieldValuesByComponentId,
    representationDataByComponentId,
    nodeLabelsData,
    nodeColorsByGraphItemId,
    conditionalFormattingState,
  };
};
